import { Link } from '@tanstack/react-router';
import { useAuthStore } from '../store/auth.ts';
import { ThemeToggle } from './ThemeToggle.tsx';
import { Icon } from './Icon.tsx';
import { t } from '@/lib/strings';

// Material 3 top app bar (desktop) — logo + wordmark/subtitle on the inline-start
// (right, RTL); theme / sign-out / avatar on the inline-end. Spans the full width
// above the navigation drawer. Mirrors WapMeet's TopBar.
export function TopBar(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  if (!user) return null;
  const shownName = user.nameDv || user.name || user.email;
  // Initials stay on the Latin name: slicing two characters out of Thaana can
  // cut a consonant away from its combining vowel mark and render as nonsense.
  const initials = (user.name || user.email).slice(0, 2).toUpperCase();

  return (
    <header
      className="hidden md:flex fixed top-0 inset-x-0 z-40 h-16 items-center gap-2 ps-3 pe-8 bg-[hsl(var(--chrome))]"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <Link to="/" className="flex items-center gap-3 ms-2 min-w-0">
        <img src="/icons/wappro-mark.svg" alt="" className="size-10 shrink-0 object-contain" />
        <span className="flex min-w-0 flex-col">
          <span className="wordmark-kolhu text-2xl text-ink leading-tight whitespace-nowrap">{t.app.name}</span>
          <span className="text-xs text-muted-foreground truncate leading-tight">{t.app.subtitle}</span>
        </span>
      </Link>

      <div className="flex-1" />

      <div className="flex items-center gap-1 shrink-0">
        <ThemeToggle />
        <button
          onClick={logout}
          aria-label={t.nav.signOut}
          className="grid size-11 place-items-center rounded-full text-muted-foreground hover:bg-muted/70 transition-colors"
        >
          <Icon name="logout" size={22} />
        </button>
        {/* WapPro has no /profile route, so the avatar chip is presentational. */}
        <span
          title={shownName}
          className="grid size-9 ms-1 place-items-center rounded-full bg-[hsl(var(--tertiary-container))] text-[hsl(var(--on-tertiary-container))] text-sm font-medium"
        >
          {initials}
        </span>
      </div>
    </header>
  );
}
